import { z } from 'zod';
import { requiredDocumentOutcomeSchema } from './supporting-documents.js';
import { intakeChannelSchema } from './enums.js';
/**
 * Self-service upload — spec v4 §25, D-016.
 *
 * A registered signatory fills in their chamber's template, signs it, and hands it over
 * without an integrator in the middle. Authentication is a magic link (Q1): typing an email
 * address is identification, not authentication, and anyone who knows an exporter's address
 * could otherwise lodge in their name.
 */
/** Step 1. The response is identical whether or not the address is known. */
export const requestUploadLinkSchema = z.object({
    chamberSlug: z.string().min(1),
    email: z.string().email(),
});
/** What the chamber's public submission page needs to render. */
export const chamberSubmitInfoSchema = z.object({
    chamberSlug: z.string(),
    chamberName: z.string(),
    /**
     * Applied as CSS custom properties at runtime, the same way the registration page does
     * it. Per-chamber theming only works if the colours arrive as tokens — a hard-coded hex
     * anywhere in the page silently breaks white-labelling (§9 of the operating manual).
     */
    primaryColor: z.string(),
    secondaryColor: z.string(),
    documentTypes: z.array(z.object({
        id: z.string(),
        code: z.string(),
        name: z.string(),
        agreement: z.string().nullable(),
        /** A blank form the exporter can download and fill in, where the chamber holds one. */
        hasTemplate: z.boolean(),
        /**
         * What this chamber expects alongside this certificate type, so the form can show the
         * list BEFORE anything is sent. An empty array means the chamber has no opinion — not
         * that nothing is needed — and the form shows no checklist at all in that case.
         */
        requiredDocuments: z
            .array(z.object({
            category: z.string(),
            categoryLabel: z.string(),
            required: z.boolean(),
            stampByDefault: z.boolean(),
            note: z.string().nullable(),
        }))
            .default([]),
    })),
});
/**
 * What a followed magic link opens onto.
 *
 * `lapsed` is a state, not an error: a signatory whose specimen window has closed cannot
 * upload, and must be told why rather than shown a generic failure. It is only ever
 * returned **after** the link is followed — saying it on the email form would confirm the
 * address exists and reopen the enumeration hole (§25.3).
 */
export const uploadSessionSchema = z.object({
    sessionToken: z.string(),
    expiresAt: z.string(),
    chamberSlug: z.string(),
    chamberName: z.string(),
    organisationName: z.string(),
    signatoryName: z.string(),
    lapsed: z.boolean(),
    lapsedOn: z.string().nullable(),
    documentTypes: chamberSubmitInfoSchema.shape.documentTypes,
    /**
     * The supporting-document categories this chamber offers, so the form can label the
     * picker without a second request. Master data, so it is the chamber's list, not ours.
     */
    supportingCategories: z
        .array(z.object({ code: z.string(), label: z.string() }))
        .default([]),
});
const fileSchema = z.object({
    filename: z.string().min(1).max(255),
    contentBase64: z.string().min(1),
});
/**
 * The submission.
 *
 * Every `keyFields` entry is optional (Q4). They restore two-sided reconciliation for the
 * fields where a mismatch matters most, but a signatory typing from a document in front of
 * them must never be able to block their own certificate with a typo in a convenience
 * field — a mismatch is a WARNING, never a FAIL.
 */
export const submitUploadSchema = z.object({
    documentTypeId: z.string().min(1),
    /**
     * The exporter's own reference, if they keep one.
     *
     * Optional: we generate a readable `UP-…` reference when it is absent. But an exporter
     * who files by their own job number needs that number to be the one they can search on,
     * and telling them "use ours instead" makes the system harder to reconcile against their
     * records than the paper process it replaces.
     *
     * Unique per organisation, not per chamber — two exporters may both use "INV-001".
     */
    senderReference: z.string().trim().min(1).max(64).optional(),
    /**
     * When they need the certificate. Defaults to today; the operator sees it as the SLA
     * target the queue is ordered by.
     */
    requestedTargetDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    /**
     * Set when this amends a certificate already issued (D-029): its number, as printed. The
     * certificate must have been issued to the uploader's organisation, and be within the
     * chamber's amendment limit; otherwise the submission is refused.
     */
    originalCertificateNo: z.string().trim().min(1).max(64).optional(),
    documentFile: fileSchema,
    supportingDocuments: z
        .array(fileSchema.extend({
        category: z.string().trim().min(1).max(60),
        /**
         * Asks the chamber to stamp this file as well as the certificate. Not an edge case:
         * for the Middle East it is mandatory and several Asian destinations ask for it, so
         * an upload channel that cannot express it is not a usable channel for those lanes.
         */
        requestSignatureAndStamp: z.boolean().default(false),
    }))
        .max(10)
        .optional(),
    keyFields: z
        .object({
        invoiceNumber: z.string().max(100).optional(),
        invoiceTotal: z.string().max(40).optional(),
        destinationCountry: z.string().length(2).optional(),
    })
        .optional(),
    /** Issued with the form, spent on submit. Stops a double-click becoming two requests. */
    submissionToken: z.string().min(1),
});
export const uploadSubmissionResultSchema = z.object({
    docId: z.string(),
    senderReference: z.string(),
    status: z.string(),
    receivedAt: z.string(),
    /** True when this reference already existed — a retry, collapsed rather than duplicated. */
    duplicate: z.boolean(),
    /**
     * What the chamber expects for this certificate type, and what is missing. Advisory: the
     * submission has already succeeded by the time this is read.
     */
    requiredDocuments: requiredDocumentOutcomeSchema.nullable().default(null),
});
export const uploadStatusRowSchema = z.object({
    docId: z.string(),
    senderReference: z.string(),
    documentTypeName: z.string(),
    status: z.string(),
    receivedAt: z.string(),
    certificateNo: z.string().nullable(),
    intakeChannel: intakeChannelSchema,
    /** Populated on a rejection, in the exporter's terms — never internal check detail. */
    outcomeNote: z.string().nullable(),
});
//# sourceMappingURL=upload.js.map